import React, { useMemo, useState } from 'react';
import { Box, Button as MuiButton, Chip, Dialog, Divider, MenuItem, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import Text from '../../../components/UI/atoms/Text';
import Button from '../../../components/UI/atoms/Button';
import Card from '../../../components/UI/molecules/Card';
import Input from '../../../components/UI/atoms/Input';
import { authPost } from '../../../services/authCalls';
import { useApp } from '../../../AppContext';

export default function SubscriptionTable({ subscriptions, plans, onCancel }) {
  const { handleMessage } = useApp();
  const [open, setOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    discountCode: '',
    trialEndDate: '',
    newProductId: '',
    newPriceId: '',
    planKey: '',
    renewalDate: '',
    subscriptionEndDate: '',
    renewalType: 'Monthly',
    subscriptionTag: 'TNM_NEWS_DATA',
    seatCount: 1,
    shippingPreference: 'Digital',
  });

  const SHIPPING_OPTIONS = ['Digital', 'Print', 'Both'];
  const RENEWAL_TYPES = ['Monthly', 'Annual'];

  const handlePlanSelect = (planKey) => {
    const plan = plans.find((_, i) => String(i) === planKey);
    if (!plan) { setForm(prev => ({ ...prev, planKey: '', newProductId: '', newPriceId: '', subscriptionTag: '', renewalType: 'Monthly' })); return; }
    setForm(prev => ({
      ...prev,
      planKey,
      newProductId: plan.ProductId || plan.productId || '',
      newPriceId: plan.PriceId || plan.priceId || '',
      subscriptionTag: plan.SubscriptionTag || plan.subscriptionTag || '',
      renewalType: plan.RenewalType || plan.renewalType || 'Monthly',
    }));
  };

  const firmId = useMemo(() => {
    const s0 = (subscriptions && subscriptions.length > 0) ? subscriptions[0] : null;
    return s0?.firmId || s0?.FirmId || '';
  }, [subscriptions]);

  const hasManualSub = useMemo(() => {
    return (subscriptions || []).some(s => {
      const id = (s?.subscriptionId || s?.SubscriptionId || '').toLowerCase();
      return id.startsWith('manual') || id.startsWith('existing') || s?.manuallyInserted === true || s?.ManuallyInserted === true;
    });
  }, [subscriptions]);

  const close = () => {
    setOpen(false);
    setModalType('');
    setSelected(null);
  };

  const openModal = (type, sub) => {
    setModalType(type);
    setSelected(sub);
    setForm(prev => ({
      ...prev,
      newProductId: '',
      newPriceId: '',
      planKey: '',
      discountCode: '',
      trialEndDate: '',
      renewalDate: sub?.renewalDate ? new Date(sub.renewalDate).toISOString().slice(0, 10) : '',
      subscriptionEndDate: sub?.subscriptionEndDate ? new Date(sub.subscriptionEndDate).toISOString().slice(0, 10) : '',
      seatCount: sub?.seat ?? sub?.Seat ?? 1,
    }));
    setOpen(true);
  };

  const run = (promise, successMsg) => {
    promise
      .then(() => {
        handleMessage('success', successMsg);
        close();
      })
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || 'Action failed';
        handleMessage('error', msg);
      });
  };

  const save = () => {
    if (!modalType) return;

    if (modalType === 'discount') {
      return run(
        authPost('/api/admin/subscription/apply-discount', {
          discountCode: form.discountCode,
          subscriptionId: selected?.subscriptionId || selected?.SubscriptionId,
          firmId: selected?.firmId || selected?.FirmId || firmId,
        }),
        'Discount applied'
      );
    }
    if (modalType === 'trial') {
      return run(
        authPost('/api/admin/subscription/update-trial', {
          subscriptionId: selected?.subscriptionId || selected?.SubscriptionId,
          firmId: selected?.firmId || selected?.FirmId || firmId,
          trialEndDate: form.trialEndDate,
        }),
        'Trial updated'
      );
    }
    if (modalType === 'change-product') {
      if (!form.newProductId || !form.newPriceId) {
        handleMessage('error', 'Select a plan first');
        return;
      }
      return run(
        authPost('/api/admin/subscription/change-product', {
          subscriptionId: selected?.subscriptionId || selected?.SubscriptionId,
          firmId: selected?.firmId || selected?.FirmId || firmId,
          newProductId: form.newProductId,
          newPriceId: form.newPriceId,
          renewalType: form.renewalType,
          shippingPreference: form.shippingPreference,
          subscriptionTag: form.subscriptionTag,
        }),
        'Subscription updated'
      );
    }
    if (modalType === 'manual-insert') {
      return run(
        authPost('/api/admin/subscription/manual/insert', {
          firmId: firmId,
          priceId: form.newPriceId,
          productId: form.newProductId,
          renewalDate: form.renewalDate ? new Date(form.renewalDate) : null,
          renewalType: form.renewalType,
          subscriptionEndDate: form.subscriptionEndDate ? new Date(form.subscriptionEndDate) : null,
          subscriptionTag: form.subscriptionTag,
          seatCount: Number(form.seatCount),
          shippingPreference: form.shippingPreference,
        }),
        'Manual subscription created'
      );
    }
    if (modalType === 'manual-update') {
      return run(
        authPost('/api/admin/subscription/manual/update', {
          firmId: firmId,
          priceId: form.newPriceId,
          productId: form.newProductId,
          renewalDate: form.renewalDate ? new Date(form.renewalDate) : null,
          renewalType: form.renewalType,
          subscriptionEndDate: form.subscriptionEndDate ? new Date(form.subscriptionEndDate) : null,
          subscriptionTag: form.subscriptionTag,
          seatCount: Number(form.seatCount),
          shippingPreference: form.shippingPreference,
        }),
        'Manual subscription updated'
      );
    }
  };

  if (!subscriptions || subscriptions.length === 0) {
    return (
      <Box sx={{ py: '16px', textAlign: 'center' }}>
        <Text sx={{ color: 'text.secondary' }}>No subscriptions found.</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size='small'>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' } }}>
              <TableCell>Subscription ID</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Renewal</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell align='right'>Seats</TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subscriptions.map((s, idx) => {
              const isActive = s?.active !== false;
              return (
                <TableRow key={s?.subscriptionId || s?.SubscriptionId || idx} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell sx={{ fontSize: '12px', fontFamily: 'monospace', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s?.subscriptionId || s?.SubscriptionId}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Text sx={{ fontSize: '13px', fontWeight: 500 }}>{s?.productName || s?.ProductName || '—'}</Text>
                      <Text sx={{ fontSize: '11px', color: 'text.secondary' }}>{s?.planName || s?.PlanName}</Text>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={isActive ? 'Active' : 'Inactive'}
                      size='small'
                      color={isActive ? 'success' : 'default'}
                      variant='outlined'
                      sx={{ fontSize: '11px', height: '20px' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '13px' }}>
                    {s?.renewalDate ? new Date(s.renewalDate).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '13px' }}>
                    {s?.SubscriptionStartDate || s?.subscriptionStartDate ? new Date(s.SubscriptionStartDate || s.subscriptionStartDate).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '13px' }}>
                    {s?.SubscriptionEndDate || s?.subscriptionEndDate ? new Date(s.SubscriptionEndDate || s.subscriptionEndDate).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell align='right'>{s?.seat ?? s?.Seat ?? '—'}</TableCell>
                  <TableCell align='right'>
                    {isActive && (
                      <Box sx={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                        <MuiButton variant='outlined' size='small' onClick={() => openModal('discount', s)}>Discount</MuiButton>
                        <MuiButton variant='outlined' size='small' onClick={() => openModal('trial', s)}>Trial</MuiButton>
                        <MuiButton variant='outlined' size='small' onClick={() => openModal('change-product', s)}>Change Plan</MuiButton>
                        <MuiButton variant='outlined' size='small' color='error' onClick={() => onCancel && onCancel(s)}>Cancel</MuiButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Divider sx={{ my: '12px' }} />
      <Box sx={{ display: 'flex', gap: '10px' }}>
        <Button type='outlined' onClick={() => openModal('manual-update', null)} disabled={!hasManualSub}>Update Manual Sub</Button>
      </Box>

      <Dialog onClose={close} open={open}>
        <Card sx={{ margin: 0, width: { xs: '320px', sm: '560px' } }}>
          <Text type='h1' sx={{ mb: '12px' }}>
            {modalType === 'discount' && 'Apply Discount Code'}
            {modalType === 'trial' && 'Update Trial End Date'}
            {modalType === 'change-product' && 'Change Subscription Plan'}
            {modalType === 'manual-insert' && 'Create Manual Subscription'}
            {modalType === 'manual-update' && 'Update Manual Subscription'}
          </Text>
          <Divider sx={{ mb: '16px' }} />

          {(modalType === 'discount') && (
            <Input label='discount code' id='discountCode' value={form.discountCode} onChange={(e) => setForm(prev => ({ ...prev, discountCode: e.target.value }))} />
          )}

          {(modalType === 'trial') && (
            <Input label='Trial End Date' id='trialEndDate' type='date' value={form.trialEndDate} onChange={(e) => setForm(prev => ({ ...prev, trialEndDate: e.target.value }))} InputLabelProps={{ shrink: true }} />
          )}

          {(modalType === 'change-product') && (
            <React.Fragment>
              <Input
                label='Select Plan'
                id='planKey'
                select
                value={form.planKey}
                onChange={e => handlePlanSelect(e.target.value)}
              >
                <MenuItem value=''>— choose a plan —</MenuItem>
                {(plans || []).map((p, i) => (
                  <MenuItem key={i} value={String(i)}>
                    {p.ProductName || p.productName} &nbsp;·&nbsp; {p.SubscriptionTag || p.subscriptionTag} &nbsp;·&nbsp; {p.RenewalType || p.renewalType}
                  </MenuItem>
                ))}
              </Input>

              {form.newProductId && (
                <Box sx={{ display: 'flex', gap: '12px', mb: '4px' }}>
                  <Box sx={{ flex: 1 }}>
                    <Text sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', mb: '2px' }}>Product ID</Text>
                    <Text sx={{ fontSize: '12px', fontFamily: 'monospace', wordBreak: 'break-all' }}>{form.newProductId}</Text>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Text sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', mb: '2px' }}>Price ID</Text>
                    <Text sx={{ fontSize: '12px', fontFamily: 'monospace', wordBreak: 'break-all' }}>{form.newPriceId}</Text>
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: '12px', mt: '8px' }}>
                <Box sx={{ flex: 1 }}>
                  <Input
                    label='Renewal Type'
                    id='renewalType'
                    select
                    value={form.renewalType}
                    onChange={e => setForm(prev => ({ ...prev, renewalType: e.target.value }))}
                  >
                    {RENEWAL_TYPES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                  </Input>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Input
                    label='Shipping Preference'
                    id='shippingPreference'
                    select
                    value={form.shippingPreference}
                    onChange={e => setForm(prev => ({ ...prev, shippingPreference: e.target.value }))}
                  >
                    {SHIPPING_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Input>
                </Box>
              </Box>
            </React.Fragment>
          )}

          {(modalType === 'manual-insert' || modalType === 'manual-update') && (
            <React.Fragment>
              <Input label='product id' id='newProductId' value={form.newProductId} onChange={(e) => setForm(prev => ({ ...prev, newProductId: e.target.value }))} />
              <Input label='price id' id='newPriceId' value={form.newPriceId} onChange={(e) => setForm(prev => ({ ...prev, newPriceId: e.target.value }))} />
              <Input label='renewal type' id='renewalType' select value={form.renewalType} onChange={(e) => setForm(prev => ({ ...prev, renewalType: e.target.value }))}>
                <MenuItem value='Monthly'>Monthly</MenuItem>
                <MenuItem value='Annual'>Annual</MenuItem>
              </Input>
              <Input label='subscription tag' id='subscriptionTag' value={form.subscriptionTag} onChange={(e) => setForm(prev => ({ ...prev, subscriptionTag: e.target.value }))} />
              <Input label='seat count' id='seatCount' value={form.seatCount} onChange={(e) => setForm(prev => ({ ...prev, seatCount: e.target.value }))} />
              <Input label='shipping preference' id='shippingPreference' value={form.shippingPreference} onChange={(e) => setForm(prev => ({ ...prev, shippingPreference: e.target.value }))} />
              <Input label='renewal date (YYYY-MM-DD)' id='renewalDate' value={form.renewalDate} onChange={(e) => setForm(prev => ({ ...prev, renewalDate: e.target.value }))} />
              <Input label='subscription end date (YYYY-MM-DD)' id='subscriptionEndDate' value={form.subscriptionEndDate} onChange={(e) => setForm(prev => ({ ...prev, subscriptionEndDate: e.target.value }))} />
            </React.Fragment>
          )}

          <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', mt: '16px' }}>
            <Button type='outlined' onClick={() => close()}>Cancel</Button>
            <Button onClick={() => save()}>Save</Button>
          </Box>
        </Card>
      </Dialog>
    </Box>
  );
}
